// Row contracts and jsonb shapes of agents, runs, approvals and finance controls (M3-01; 02
// §Identity (agent rows), §Approvals, runs, cost, §Finance; 11). Same conventions as `rows.ts`:
// keys are column names, nullability is the column's, enums are the CHECK's; bound to their
// tables in `contract-bindings.ts`.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import { PointerRef } from "./de-config-rows";
import {
  AgentProfileOwnerKind,
  AgentProfileRole,
  AgentProfileState,
  AgentTemplateKind,
  AgentTemplateScope,
  AgentTemplateStatus,
  ApprovalExecutionCode,
  ApprovalExecutionFailure,
  ApprovalExecutionState,
  ApprovalKind,
  ApprovalState,
  BudgetIncidentKind,
  BudgetPeriodType,
  BudgetState,
  BudgetSubjectKind,
  Currency,
  DelegationState,
  MaintenanceTier,
  PlanCheckVerdict,
  PriceListScope,
  PriceUnit,
  ReservationState,
  RunEffortRequest,
  RunEffortSource,
  RunEffortTier,
  RunIdentityMode,
  RunModelPath,
  RunRetryState,
  RunState,
  RunStepKind,
  SandboxKind,
  TemplateClass,
  Verdict,
  WakeTimerStatus,
  WakeupHoldReason,
  WakeupKind,
  WakeupStatus,
  WakeupSteer,
} from "./enums";
import {
  BoundedJson,
  Decimal,
  IsoDate,
  LIMITS,
  MicrosAmount,
  MinorAmount,
  Timestamp,
  text,
  Uuid,
} from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

// jsonb shapes ----------------------------------------------------------------------------------

/** `{kind, id}` of what caused a wake-up or a run (message, task, approval, …). */
export const SourceRefJson = z
  .strictObject({
    kind: z.enum([
      "message",
      "topic",
      "task",
      "approval",
      "delegation_request",
      "inbox_item",
      "schedule",
      "run",
      "manual",
      // M4-05: a room's turn (one live run per agent per room) and a status ask to answer.
      "agent_room",
      "status_ask",
      // AGENT-REPORTING: the reporting duty whose due time woke the agent (`reporting` wake-ups).
      "reporting_duty",
      // MEETINGS: the pre-read of a meeting, asked of the team's agent the day before.
      "meeting",
      // CHAT-SWITCH-B: a question a person put to the DE from the search page ("Ask").
      "chat_ask",
      // DE-BRIEFS: the brief a DE with a runtime is woken to write in its own words.
      "de_brief",
      // T2-BOARD-WAKE: the board its owning DE is woken to look at.
      "board",
      // CLOSED-LOOPS: a commitment the DE owes, asked, verifies or must decide on (no other subject).
      "commitment",
    ]),
    id: Uuid.nullable(),
  })
  .meta({ id: "WakeupSourceRef" });
export type SourceRefJson = z.infer<typeof SourceRefJson>;

/** A pointer handed to a delegate (never content): what it may read for the task. */
export const ContextRef = z
  .strictObject({
    /** TASK-FILES: `attachment` — a task file a run produced (Work OS `artifactRefs`). */
    kind: z.enum(["task", "message", "page", "thread", "object", "memory", "attachment"]),
    id: z.string().min(1).max(256),
  })
  .meta({ id: "ContextRef" });
export type ContextRef = z.infer<typeof ContextRef>;

/** What a finished run hands back to its requester (04 `complete {summary, hand_back}`). */
export const RunHandBack = z
  .strictObject({
    summary: z.string().max(LIMITS.text).optional(),
    outputs: z.array(ContextRef).max(LIMITS.list).optional(),
    next_steps: z.array(text(500)).max(20).optional(),
    needs: z.enum(["nothing", "review", "input", "approval"]).optional(),
  })
  .meta({ id: "RunHandBack" });
export type RunHandBack = z.infer<typeof RunHandBack>;

/**
 * An approval's proposal (02 §approval). `change` is the kind-specific payload the executor
 * applies (a task patch, a message body, a budget raise `{extra_minor, until}`, …).
 */
export const ApprovalProposal = z
  .strictObject({
    title: text(LIMITS.name),
    summary: z.string().max(LIMITS.text).optional(),
    change: BoundedJson,
    /**
     * M3-03, DC-05 (ADR-0019 §2): raised by an unverified DE (a pilot under a verification
     * override, or an empty self-serve agent); approval cards show the **unverified** badge. Set
     * by the server from the DE's mirror, never by the proposer or the run.
     */
    unverified: z.boolean().optional(),
    /**
     * COS-MODES: an Ask-me-first request (an agent directing another person's agent): what to do
     * when it expires undecided (`proceed`: apply it; `escalate`: ask the CEO), set by the server.
     */
    direction: z
      .strictObject({
        action: z.enum(["assign", "priority"]),
        head_membership_id: Uuid,
        actor_membership_id: Uuid.nullable(),
        principal_membership_id: Uuid.nullable(),
        fallback: z.enum(["proceed", "escalate"]),
        /** An assignment: the agent seat it hands to and the seat that hands it (if assigned). */
        to_seat_id: Uuid.nullable(),
        from_seat_id: Uuid.nullable(),
        /** Set on the CEO's copy after an escalation (it never escalates twice). */
        escalated: z.boolean().optional(),
      })
      .optional(),
    /**
     * PLAN-CHECK: a DE's request carries the latest `plan_check` of its run (Recall's verdict on
     * the plan against past decisions and rules); absent when the run asked none. Set by the
     * server from `memory_plan_check`, never by the proposer; an edit keeps it.
     */
    plan_check: z
      .strictObject({
        verdict: PlanCheckVerdict,
        check_id: Uuid.nullable(),
        plan: z.string().max(2000).nullable(),
        conflicts: z
          .array(z.strictObject({ type: z.string().max(80), title: z.string().max(300) }))
          .max(5),
        checked_at: Timestamp.nullable(),
      })
      .optional(),
  })
  .meta({ id: "ApprovalProposal" });
export type ApprovalProposal = z.infer<typeof ApprovalProposal>;

/**
 * CORE-CARDS (flag `escalation_cards`, core delivery plan item 2): one option of an escalation
 * card. `decision` says what choosing it does (approve, the default: the proposal goes ahead,
 * with `change` in place of the proposal's when given; reject: nothing is done, the agent is told
 * the label). `key` is what the decision stores (`approval.chosen_option`).
 */
export const ApprovalCardOption = z
  .strictObject({
    key: z
      .string()
      .max(40)
      .regex(/^[a-z0-9][a-z0-9_-]{0,39}$/),
    label: text(80),
    consequence: text(300),
    decision: z.enum(["approve", "reject"]).optional(),
    change: BoundedJson.optional(),
  })
  .meta({ id: "ApprovalCardOption" });
export type ApprovalCardOption = z.infer<typeof ApprovalCardOption>;

/**
 * CORE-CARDS: a ruling that applies to the card's case (lane B days 4–6 fill it from Recall's
 * `rulings/match`; until then it is empty). Set by the server, never by the proposer.
 */
export const ApprovalCardPrecedent = z
  .strictObject({
    record_id: z.string().min(1).max(256),
    match: z.enum(["structural", "similar"]),
    summary: z.string().max(300),
    option: z.string().max(80).nullable(),
    standing: z.boolean(),
    approver: z.string().max(200).nullable(),
    at: Timestamp.nullable(),
  })
  .meta({ id: "ApprovalCardPrecedent" });
export type ApprovalCardPrecedent = z.infer<typeof ApprovalCardPrecedent>;

/**
 * CORE-CARDS: what a proposer (an agent's `propose`, `POST approvals`, the policy gate's
 * `defaultCard`) supplies. Keys unique and `recommended` one of them (checked on filing).
 * `deadline` becomes the approval's `expires_at` when the filing names none.
 */
export const ApprovalCardInput = z
  .strictObject({
    situation: text(1000),
    options: z.array(ApprovalCardOption).min(2).max(3),
    recommended: z.string().max(40),
    deadline: Timestamp.optional(),
  })
  .meta({ id: "ApprovalCardInput" });
export type ApprovalCardInput = z.infer<typeof ApprovalCardInput>;

/** CORE-CARDS: the card as stored (`approval.card`): the input plus what the server resolved. */
export const ApprovalCard = z
  .strictObject({
    situation: z.string().max(1000),
    options: z.array(ApprovalCardOption).min(2).max(3),
    recommended: z.string().max(40),
    /** The approver seat as resolved at filing (the approval's `approver_seat_id`). */
    approver_seat_id: Uuid,
    /** The approval's `expires_at` at filing; null: no deadline. */
    deadline: Timestamp.nullable(),
    /** The rulings that apply (CORE-PRECEDENTS: matched at filing; empty while off). */
    precedents: z.array(ApprovalCardPrecedent).max(5),
    /** The Slack post announcing it (null: not posted, or no channel set). */
    slack: z
      .strictObject({ channel: z.string().max(40), ts: z.string().max(40), at: Timestamp })
      .nullable(),
  })
  .meta({ id: "ApprovalCard" });
export type ApprovalCard = z.infer<typeof ApprovalCard>;

/**
 * CORE-PRECEDENTS (flag `precedents`, core delivery plan item 3): the structured facts of a case,
 * flat (`{discount_pct: 20, offer: "retainer"}`). A ruling's facts are matched key by key; a
 * numeric fact of a ruling may be a range (`{gte, lte}`), matched by the case's number.
 */
export const RulingFactValue = z.union([
  z.string().max(200),
  z.number(),
  z.boolean(),
  z
    .strictObject({ gte: z.number().optional(), lte: z.number().optional() })
    .refine((r) => r.gte !== undefined || r.lte !== undefined, "a range has gte or lte"),
]);
export type RulingFactValue = z.infer<typeof RulingFactValue>;
export const RulingFacts = z
  .record(
    z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z0-9_.-]+$/),
    RulingFactValue,
  )
  .refine((r) => Object.keys(r).length <= 20, "at most 20 facts")
  .meta({ id: "RulingFacts", maxProperties: 20 });
export type RulingFacts = z.infer<typeof RulingFacts>;

/**
 * CORE-PRECEDENTS: the condition of a case (what a ruling decided, what a proposal asks): the
 * action class, the scope (`company`, `board:{id}`, `seat:{id}`), the subject kind, the facts.
 */
export const RulingCondition = z
  .strictObject({
    class: z.string().min(1).max(64),
    scope: z
      .string()
      .max(80)
      .regex(/^(company|(board|seat|space):[0-9a-f-]{36})$/),
    subject_kind: z.string().min(1).max(40),
    facts: RulingFacts,
  })
  .meta({ id: "RulingCondition" });
export type RulingCondition = z.infer<typeof RulingCondition>;

/**
 * CORE-PRECEDENTS: what a proposer says about its case (the filing completes it: the class
 * defaults to the approval kind, the scope to `company`, the subject kind is the approval's).
 */
export const RulingConditionInput = z
  .strictObject({
    class: z.string().min(1).max(64).optional(),
    scope: RulingCondition.shape.scope.optional(),
    facts: RulingFacts,
  })
  .meta({ id: "RulingConditionInput" });
export type RulingConditionInput = z.infer<typeof RulingConditionInput>;

/** `budget_reservation.chain`: one hold per level, agent → … → company. */
export const ReservationChain = z
  .array(
    z.strictObject({
      level: BudgetSubjectKind,
      budget_id: Uuid,
      amount_micros: MicrosAmount,
    }),
  )
  .max(5)
  .meta({ id: "ReservationChain" });
export type ReservationChain = z.infer<typeof ReservationChain>;

// cp catalog ------------------------------------------------------------------------------------

export const AgentTemplate = z
  .object({
    id: Uuid,
    scope: AgentTemplateScope,
    agency_id: Uuid.nullable(),
    key: z.string(),
    name: z.string(),
    version: z.int().min(1),
    kind: AgentTemplateKind,
    status: AgentTemplateStatus,
    instructions_ref: z.string().nullable(),
    skill_pack_ids: z.array(Uuid),
    tool_grants: z.record(z.string(), z.unknown()),
    model_policy: z.record(z.string(), z.unknown()),
    runtime_config: z.record(z.string(), z.unknown()),
    license_note: z.string().nullable(),
    published_at: Timestamp.nullable(),
    deprecated_at: Timestamp.nullable(),
    created_by_user_id: Uuid.nullable(),
    current_version_id: Uuid.nullable(),
    template_class: TemplateClass,
    /** BILLING-PLANS: the maintenance tier a DE made from it starts on (null = standard). */
    maintenance_tier: MaintenanceTier.nullable(),
    /** BILLING-PLANS: the setup fee on DE creation in cents (null = the platform default). */
    setup_fee_minor: MinorAmount.nullable(),
    ...stamps,
  })
  .meta({ id: "AgentTemplate" });
export type AgentTemplate = z.infer<typeof AgentTemplate>;

// Agents and runs -------------------------------------------------------------------------------

export const AgentProfile = z
  .object({
    company_id: Uuid,
    id: Uuid,
    template_id: Uuid,
    template_version: z.int().min(1),
    kind: AgentTemplateKind,
    owner_kind: AgentProfileOwnerKind,
    owner_membership_id: Uuid,
    role_in_owner: AgentProfileRole,
    overrides: z.record(z.string(), z.unknown()),
    memory_group_id: z.string().nullable(),
    state: AgentProfileState,
    ...actor,
    ...stamps,
  })
  .meta({ id: "AgentProfile" });
export type AgentProfile = z.infer<typeof AgentProfile>;

export const WakeupRequest = z
  .object({
    company_id: Uuid,
    id: Uuid,
    membership_id: Uuid,
    kind: WakeupKind,
    source_ref: SourceRefJson.nullable(),
    idempotency_key: z.string().nullable(),
    status: WakeupStatus,
    run_id: Uuid.nullable(),
    drop_reason: z.string().nullable(),
    /** MID-RUN-STEER: how a person's message reached a running run (null: the normal queue). */
    steer: WakeupSteer.nullable(),
    /** RUN-EFFORT: the effort its producer asked for (null: auto). */
    effort: RunEffortRequest.nullable(),
    /** PAUSE-NO-DROP: why a queued wake-up is held (its DE paused, out of budget or credit). */
    hold_reason: WakeupHoldReason.nullable(),
    requested_at: Timestamp,
    ...actor,
    ...stamps,
  })
  .meta({ id: "WakeupRequest" });
export type WakeupRequest = z.infer<typeof WakeupRequest>;

/** AGENT-PLUMBING: a DE's "wake me after N minutes" (`runs.wakeAfter`, job `wake_timer.fire`). */
export const WakeTimer = z
  .object({
    company_id: Uuid,
    id: Uuid,
    membership_id: Uuid,
    run_id: Uuid.nullable(),
    source_ref: SourceRefJson,
    reason: z.string(),
    due_at: Timestamp,
    status: WakeTimerStatus,
    wakeup_id: Uuid.nullable(),
    fired_at: Timestamp.nullable(),
    cancel_reason: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "WakeTimer" });
export type WakeTimer = z.infer<typeof WakeTimer>;

/** FAILURE-STOP: `run.failure_guard` as served (domain/runs/failure-stop.ts). */
export const RunFailureGuard = z
  .object({
    total: z.int().min(0).optional(),
    streak: z.int().min(0).optional(),
    by_tool: z.record(z.string(), z.int()).optional(),
    refusals: z.record(z.string(), z.int()).optional(),
    helpers: z.record(z.string(), z.int()).optional(),
    notes: z.int().min(0).optional(),
    continued: z.int().min(0).optional(),
    stopped: z
      .object({ kind: z.string(), reason: z.string(), at: z.string() })
      .nullable()
      .optional(),
  })
  .meta({ id: "RunFailureGuard" });

export const Run = z
  .object({
    company_id: Uuid,
    id: Uuid,
    membership_id: Uuid,
    crew_agent_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    agent_profile_id: Uuid.nullable(),
    owner_membership_id: Uuid.nullable(),
    identity_mode: RunIdentityMode,
    trigger_kind: WakeupKind,
    trigger_ref: SourceRefJson.nullable(),
    wakeup_ids: z.array(Uuid),
    state: RunState,
    state_reason: z.string().nullable(),
    started_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    parent_run_id: Uuid.nullable(),
    delegation_depth: z.int().min(0),
    budget_reserved_micros: MicrosAmount,
    cost_micros: MicrosAmount,
    tokens_in: z.int().min(0),
    tokens_out: z.int().min(0),
    model: z.string().nullable(),
    /** RUN-EFFORT: the tier the run got and why (null: admitted before tiers). */
    effort: RunEffortTier.nullable(),
    effort_source: RunEffortSource.nullable(),
    /** RUN-EFFORT: the caller named the model (an effort change never re-routes it). */
    model_pinned: z.boolean(),
    sandbox_kind: SandboxKind.nullable(),
    skill_pack_versions: z.record(z.string(), z.string()),
    summary: z.string().nullable(),
    hand_back: RunHandBack.nullable(),
    lease_owner: z.string().nullable(),
    lease_until: Timestamp.nullable(),
    // DC-01 (ADR-0019, ADR-0020)
    verdict: Verdict.nullable(),
    verdict_evidence_ref: PointerRef.nullable(),
    output_ref: PointerRef.nullable(),
    model_path: RunModelPath,
    model_path_fallback_used: z.boolean(),
    model_credential_id: Uuid.nullable(),
    // DC-08 (05 §Delegation rules 8): an ephemeral worker's scope — the only refs its token
    // reads and the deployed bundle its standing orders were copied from; null for other runs.
    context_refs: z.array(ContextRef).nullable(),
    bundle_sha: z.string().nullable(),
    // AGENT-STABILITY: the automatic successors of a run lost to infrastructure (retry.ts).
    attempt: z.int().min(1),
    retry_of_run_id: Uuid.nullable(),
    retried_by_run_id: Uuid.nullable(),
    retry_state: RunRetryState.nullable(),
    retry_after: Timestamp.nullable(),
    retry_reason: z.string().nullable(),
    // ACTIVITY: the task the run works on (its trigger's, else its first task event's).
    task_id: Uuid.nullable(),
    // FAILURE-STOP: the run's tool-error counters and its stop after repeated failures (the
    // run page shows Continue while `stopped` is set); null when nothing failed.
    failure_guard: RunFailureGuard.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Run" });
export type Run = z.infer<typeof Run>;

export const RunStep = z
  .object({
    company_id: Uuid,
    id: Uuid,
    run_id: Uuid,
    seq: z.int().min(1),
    kind: RunStepKind,
    payload: z.record(z.string(), z.unknown()),
    cost_micros: MicrosAmount,
    tokens_in: z.int().min(0),
    tokens_out: z.int().min(0),
    model: z.string().nullable(),
    duration_ms: z.int().min(0).nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "RunStep" });
export type RunStep = z.infer<typeof RunStep>;

/**
 * RECALL-RUN-LIFECYCLE: a DE's proposal checked against its memory (Recall's `plan_check` under the
 * DE's own key, over its reach). `conflict` means a recorded decision or rule says otherwise: the
 * approval then waits for a person even where it would have gone ahead on its own at expiry.
 */
export const ApprovalPlanCheck = z
  .object({
    verdict: z.enum(["conflict", "caution", "no_conflict_found", "unjudged", "unavailable"]),
    /** The records that conflict (at most 5): type, title, space name and why. */
    conflicts: z.array(
      z.object({
        type: z.string(),
        title: z.string(),
        space: z.string().nullable(),
        reason: z.string().nullable(),
      }),
    ),
    guidance: z.string().nullable(),
    /** Why there is no verdict (`unavailable`): memory off, no key, Recall did not answer. */
    detail: z.string().nullable(),
    /** A conflict that held this approval for a person (its expiry no longer goes ahead). */
    held: z.boolean(),
    checked_at: Timestamp,
  })
  .meta({ id: "ApprovalPlanCheck" });
export type ApprovalPlanCheck = z.infer<typeof ApprovalPlanCheck>;

export const Approval = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: ApprovalKind,
    subject_kind: z.string(),
    subject_id: Uuid,
    requested_by_seat_id: Uuid.nullable(),
    requested_by_membership_id: Uuid.nullable(),
    approver_seat_id: Uuid,
    run_id: Uuid.nullable(),
    proposal: ApprovalProposal,
    state: ApprovalState,
    approved_payload: ApprovalProposal.nullable(),
    decided_at: Timestamp.nullable(),
    decided_by_membership_id: Uuid.nullable(),
    reason: z.string().nullable(),
    /** B2-GATES: rejected as "send back with a note" (`reason` is the note). */
    sent_back: z.boolean(),
    thread_id: Uuid.nullable(),
    expires_at: Timestamp.nullable(),
    executed_at: Timestamp.nullable(),
    // Phase 1 inc 2: the execution outcome, apart from the decision (`state`). NULL while there
    // is nothing to apply (undecided, rejected, expired). `failed` carries its class and reason:
    // "Approved · not applied: <reason>" is a decided approval whose change did not happen.
    execution_state: ApprovalExecutionState.nullable().describe(
      "What became of the approved payload: pending, applied or failed (null: nothing to apply)",
    ),
    execution_failure: ApprovalExecutionFailure.nullable().describe(
      "When failed: permanent (never retried) or transient (retried)",
    ),
    execution_code: ApprovalExecutionCode.nullable(),
    execution_reason: z.string().nullable(),
    execution_attempts: z.int().min(0),
    execution_retry_at: Timestamp.nullable(),
    plan_check: ApprovalPlanCheck.nullable().describe(
      "RECALL-RUN-LIFECYCLE: a DE's proposal checked against its memory (null: not checked)",
    ),
    card: ApprovalCard.nullable().describe(
      "CORE-CARDS: the escalation card (situation, options, recommended, approver, deadline); null: none",
    ),
    chosen_option: z
      .string()
      .nullable()
      .describe("CORE-CARDS: the key of the card option the decider chose"),
    condition: RulingCondition.nullable().describe(
      "CORE-PRECEDENTS: the case's condition (class, scope, subject kind, facts), matched against rulings; null: none given",
    ),
    precedent_ids: z
      .array(z.string())
      .nullable()
      .describe(
        "CORE-PRECEDENTS: the rulings matched at filing (cited or followed); null: not matched",
      ),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Approval" });
export type Approval = z.infer<typeof Approval>;

export const DelegationRequest = z
  .object({
    company_id: Uuid,
    id: Uuid,
    from_membership_id: Uuid,
    to_membership_id: Uuid,
    to_seat_id: Uuid.nullable(),
    from_run_id: Uuid.nullable(),
    message_id: Uuid.nullable(),
    task_id: Uuid.nullable(),
    context_refs: z.array(ContextRef),
    budget_micros: MicrosAmount,
    deadline_at: Timestamp.nullable(),
    state: DelegationState,
    depth: z.int().min(1),
    root_request_id: Uuid,
    child_run_id: Uuid.nullable(),
    result: RunHandBack.nullable(),
    decided_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "DelegationRequest" });
export type DelegationRequest = z.infer<typeof DelegationRequest>;

// Finance controls ------------------------------------------------------------------------------

export const Budget = z
  .object({
    company_id: Uuid,
    id: Uuid,
    subject_kind: BudgetSubjectKind,
    subject_id: Uuid,
    period_type: BudgetPeriodType,
    period_key: z.string().nullable(),
    amount_minor: MinorAmount,
    currency: Currency,
    soft_thresholds: z.array(Decimal),
    hard_cap_minor: MinorAmount.nullable(),
    inherit: z.boolean(),
    /** DE-BUDGET-PACE: a DE's own budget alerts on pace but never stops its runs. */
    alert_only: z.boolean(),
    /** BUDGET-ROLLOVER: the next period's budget is created when this one ends. */
    repeat_monthly: z.boolean(),
    state: BudgetState,
    ...actor,
    ...stamps,
  })
  .meta({ id: "Budget" });
export type Budget = z.infer<typeof Budget>;

export const BudgetReservation = z
  .object({
    company_id: Uuid,
    id: Uuid,
    run_id: Uuid,
    chain: ReservationChain,
    estimate_micros: MicrosAmount,
    settled_micros: MicrosAmount.nullable(),
    state: ReservationState,
    expires_at: Timestamp,
    settled_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "BudgetReservation" });
export type BudgetReservation = z.infer<typeof BudgetReservation>;

export const BudgetIncident = z
  .object({
    company_id: Uuid,
    id: Uuid,
    budget_id: Uuid,
    kind: BudgetIncidentKind,
    threshold: Decimal.nullable(),
    period_key: z.string().nullable(),
    run_id: Uuid.nullable(),
    amount_minor: MinorAmount,
    resolved_by_approval_id: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "BudgetIncident" });
export type BudgetIncident = z.infer<typeof BudgetIncident>;

export const PriceList = z
  .object({
    company_id: Uuid,
    id: Uuid,
    scope: PriceListScope,
    source_price_id: Uuid.nullable(),
    unit: PriceUnit,
    model: z.string().nullable(),
    price_micros: MicrosAmount,
    /** HTVA-RUN-FIXES: the units `price_micros` is for (1, or 1,000,000 for per-million). */
    per_units: z.int().min(1),
    /** PRICE-TIERS: above this prompt size (tokens per call) the tier rate applies. */
    tier_above_tokens: z.int().min(1).nullable(),
    tier_price_micros: MicrosAmount.nullable(),
    currency: Currency,
    effective_from: IsoDate,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "PriceList" });
export type PriceList = z.infer<typeof PriceList>;

export const FxRate = z
  .object({
    company_id: Uuid,
    period_key: z.string(),
    from_currency: Currency,
    to_currency: Currency,
    rate: Decimal,
    source: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "FxRate" });
export type FxRate = z.infer<typeof FxRate>;

// AGENT-MENTION: wake results and readiness (views, not rows) --------------------------------

/**
 * What became of one ask of an agent (06 §Agents in chat; 02 `wakeup_request`; 05 §Delegation):
 * a message that mentions it, or a task handed to its seat. Never silent: an ask that woke
 * nobody says why. Started: `queued` (waiting for admission or a free slot), `running`,
 * `waiting` (on an approval or a reply), `done`, `failed`. Not started: `not_live` (still in
 * setup, retired, or the Agents add-on is off), `no_access` (it cannot read the conversation;
 * a mention never grants access), `paused`, `over_budget`, `silent` (the channel's agents run on
 * schedule only), `blocked` (refused by a policy, `reason` says which), `not_in_reach` (the
 * agent does not take asks from this person: REACH-S1, an access request can be sent).
 */
export const AgentWakeState = z
  .enum([
    "queued",
    "running",
    "waiting",
    "done",
    "failed",
    "not_live",
    "no_access",
    "paused",
    "over_budget",
    "silent",
    "blocked",
    // REACH-S1: the agent does not take asks from this person (its Hears from); ask for access
    "not_in_reach",
  ])
  .meta({ id: "AgentWakeState" });
export type AgentWakeState = z.infer<typeof AgentWakeState>;

/** The agent principals a mention or a task reaches. */
export const AgentPrincipalKind = z
  .enum(["digital_employee", "personal_agent"])
  .meta({ id: "AgentPrincipalKind" });

export const AgentWake = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    agent_kind: AgentPrincipalKind,
    state: AgentWakeState,
    /** One short sentence for the person who asked ("Riley is still being set up."). */
    reason: z.string(),
    run_id: Uuid.nullable(),
    /** When the wake-up was asked; null when nothing was (a task's readiness). */
    requested_at: Timestamp.nullable(),
    /**
     * MID-RUN-STEER: where a person's message went while the agent was busy or about to start —
     * `running_turn` (folded into the run that was working, flag `de_mid_run_steer`) or
     * `next_turn` (waiting for the agent's next run, batched with the others sent meanwhile).
     * Absent: neither applies.
     */
    delivery: z.enum(["running_turn", "next_turn"]).optional(),
  })
  .meta({ id: "AgentWake" });
export type AgentWake = z.infer<typeof AgentWake>;

/**
 * Before the ask (the @ menu, the assignee picker): would a mention here wake this agent now?
 * `ready`, or the state a mention would end in and why.
 */
export const AgentReadinessState = z
  .enum(["ready", "not_live", "no_access", "paused", "silent", "not_in_reach"])
  .meta({ id: "AgentReadinessState" });
export type AgentReadinessState = z.infer<typeof AgentReadinessState>;
